/**
 * SlideRenderer.jsx
 * =================
 * Renderer générique pour les slides de formation pédagogique.
 * Chaque slide est définie par un objet JSON avec un `type` qui mappe vers
 * un composant React typé. Le contenu reste 100 % data-driven (i18n & versionning faciles).
 *
 * Types supportés :
 *   - cover         : slide de garde (titre, sous-titre, durée, icône)
 *   - intro         : présentation des objectifs du module
 *   - concept       : explication d'un concept avec bullets
 *   - screenshot    : capture d'écran annotée avec callouts
 *   - steps         : tutoriel pas-à-pas numéroté
 *   - checklist     : liste de tâches à cocher / bonnes pratiques
 *   - comparison    : tableau ✅ Bonne pratique vs ❌ À éviter
 *   - quiz          : quiz interactif (auto-correctif)
 *   - takeaways     : récap "À retenir" en encadré
 *   - cta           : appel à l'action final (vers une URL/page)
 */
import React, { useState } from 'react';
import {
  Truck, ClipboardList, Zap, AlertTriangle, CheckCircle, XCircle,
  ArrowRight, BookOpen, Lightbulb, Target, Award, ChevronRight,
  HelpCircle, ListChecks, FileText, BarChart3, Users, Shield, DollarSign,
} from 'lucide-react';

const ICON_MAP = {
  Truck, ClipboardList, Zap, AlertTriangle, CheckCircle, BookOpen, Lightbulb,
  Target, Award, HelpCircle, ListChecks, FileText, BarChart3, Users, Shield, DollarSign,
};

const resolveIcon = (name) => ICON_MAP[name] || BookOpen;

// ---- Slide : Cover (page de garde) ---------------------------------------
const CoverSlide = ({ data, theme }) => {
  const Icon = resolveIcon(data.icon);
  return (
    <div className={`h-full w-full flex flex-col items-center justify-center text-center px-12 py-10 bg-gradient-to-br ${theme.gradient}`}>
      <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl ${theme.iconBg} mb-6 shadow-lg`}>
        <Icon className={`w-10 h-10 ${theme.iconColor}`} />
      </div>
      <p className={`text-xs uppercase tracking-[0.3em] font-semibold mb-2 ${theme.accent}`}>{data.eyebrow}</p>
      <h1 className="text-5xl font-extrabold text-slate-900 leading-tight mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
        {data.title}
      </h1>
      <p className="text-xl text-slate-600 max-w-3xl mb-6">{data.subtitle}</p>
      <div className="flex items-center gap-4 text-sm text-slate-500">
        {data.duration && <span className="inline-flex items-center gap-1.5"><ListChecks className="w-4 h-4" /> {data.duration}</span>}
        {data.audience && <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {data.audience}</span>}
        {data.level && <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4" /> {data.level}</span>}
      </div>
    </div>
  );
};

// ---- Slide : Intro (objectifs du module) ---------------------------------
const IntroSlide = ({ data, theme }) => (
  <div className="h-full w-full px-12 py-10 bg-white">
    <p className={`text-xs uppercase tracking-[0.3em] font-semibold mb-2 ${theme.accent}`}>Objectifs</p>
    <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
    <p className="text-lg text-slate-600 mb-8 max-w-4xl">{data.intro}</p>
    <div className="grid grid-cols-2 gap-4">
      {(data.objectives || []).map((obj, i) => (
        <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${theme.border} bg-slate-50/50`}>
          <div className={`flex-shrink-0 w-8 h-8 rounded-full ${theme.iconBg} flex items-center justify-center font-bold ${theme.iconColor}`}>{i + 1}</div>
          <p className="text-base text-slate-700">{obj}</p>
        </div>
      ))}
    </div>
  </div>
);

// ---- Slide : Concept (titre + texte + bullets) ---------------------------
const ConceptSlide = ({ data, theme }) => {
  const Icon = resolveIcon(data.icon);
  return (
    <div className="h-full w-full px-12 py-10 bg-white">
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`w-5 h-5 ${theme.iconColor}`} />
        <p className={`text-xs uppercase tracking-[0.3em] font-semibold ${theme.accent}`}>{data.eyebrow || 'Concept'}</p>
      </div>
      <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
      {data.lede && <p className="text-lg text-slate-600 mb-6 max-w-4xl">{data.lede}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mt-4">
        {(data.bullets || []).map((b, i) => (
          <div key={i} className="flex items-start gap-3">
            <ChevronRight className={`w-5 h-5 mt-0.5 flex-shrink-0 ${theme.iconColor}`} />
            <p className="text-base text-slate-700">{b}</p>
          </div>
        ))}
      </div>
      {data.note && (
        <div className={`mt-6 px-4 py-3 rounded-lg border-l-4 ${theme.noteBorder} bg-slate-50 text-sm text-slate-700`}>
          <Lightbulb className={`inline w-4 h-4 mr-2 ${theme.iconColor}`} /> <strong>Astuce :</strong> {data.note}
        </div>
      )}
    </div>
  );
};

// ---- Slide : Screenshot annoté ------------------------------------------
const ScreenshotSlide = ({ data, theme }) => {
  const [focused, setFocused] = useState(null);
  // Si callouts ont des coords {x, y}, on les positionne en overlay sur l'image
  // Sinon on retombe sur le layout "image + colonne latérale" classique
  const hasPositioned = (data.callouts || []).some((c) => typeof c.x === 'number' && typeof c.y === 'number');
  return (
    <div className="h-full w-full px-10 py-8 bg-white flex flex-col">
      <p className={`text-xs uppercase tracking-[0.3em] font-semibold mb-1 ${theme.accent}`}>{data.eyebrow || 'Aperçu interface'}</p>
      <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
      <div className={`flex-1 flex ${hasPositioned ? 'flex-col' : 'gap-6'} min-h-0`}>
        <div className={`${hasPositioned ? 'flex-1' : 'flex-1'} relative bg-slate-100 rounded-xl border border-slate-200 overflow-hidden flex items-center justify-center`}>
          {data.image ? (
            <>
              <img src={data.image} alt={data.title} className="max-w-full max-h-full object-contain" />
              {hasPositioned && (data.callouts || []).map((c, i) => (
                typeof c.x === 'number' && typeof c.y === 'number' ? (
                  <button
                    key={i}
                    type="button"
                    onMouseEnter={() => setFocused(i)}
                    onMouseLeave={() => setFocused(null)}
                    onFocus={() => setFocused(i)}
                    onBlur={() => setFocused(null)}
                    aria-label={`Marqueur ${i + 1} : ${c.label}`}
                    className="absolute group transition-transform"
                    style={{ left: `${c.x}%`, top: `${c.y}%`, transform: 'translate(-50%, -50%)' }}
                    data-testid={`screenshot-marker-${i + 1}`}
                  >
                    <span className={`flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm text-white shadow-lg ring-2 ring-white ${focused === i ? 'scale-125' : ''} transition-transform`}
                          style={{ backgroundColor: theme.iconBg.includes('emerald') ? '#059669' : '#4f46e5' }}>
                      {i + 1}
                    </span>
                    {focused === i && (
                      <div className="absolute z-10 left-full ml-2 top-1/2 -translate-y-1/2 w-64 bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs whitespace-normal text-left">
                        <p className="font-semibold mb-0.5">{c.label}</p>
                        <p className="opacity-80">{c.text}</p>
                      </div>
                    )}
                  </button>
                ) : null
              ))}
            </>
          ) : (
            <div className="text-slate-400 text-sm italic">Capture d'écran à venir</div>
          )}
        </div>
        {(data.callouts || []).length > 0 && (
          <div className={`${hasPositioned ? 'mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2' : 'w-1/3 flex flex-col gap-3 overflow-y-auto'}`}>
            {data.callouts.map((c, i) => (
              <div
                key={i}
                onMouseEnter={() => setFocused(i)}
                onMouseLeave={() => setFocused(null)}
                className={`p-2.5 rounded-lg border-l-4 ${theme.noteBorder} bg-slate-50 transition-all ${focused === i ? 'bg-slate-100 shadow-sm' : ''}`}
              >
                <p className="text-xs font-semibold text-slate-900">{i + 1}. {c.label}</p>
                <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">{c.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Slide : Étapes numérotées ------------------------------------------
const StepsSlide = ({ data, theme }) => (
  <div className="h-full w-full px-12 py-10 bg-white">
    <p className={`text-xs uppercase tracking-[0.3em] font-semibold mb-1 ${theme.accent}`}>{data.eyebrow || 'Mode opératoire'}</p>
    <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
    <div className="space-y-3">
      {(data.steps || []).map((step, i) => (
        <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${theme.iconBg} flex items-center justify-center font-bold text-lg ${theme.iconColor}`}>{i + 1}</div>
          <div className="flex-1">
            <p className="font-semibold text-slate-900">{step.title}</p>
            {step.detail && <p className="text-sm text-slate-600 mt-1">{step.detail}</p>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ---- Slide : Checklist (bonnes pratiques) -------------------------------
const ChecklistSlide = ({ data, theme }) => (
  <div className="h-full w-full px-12 py-10 bg-white">
    <p className={`text-xs uppercase tracking-[0.3em] font-semibold mb-1 ${theme.accent}`}>Bonnes pratiques</p>
    <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {(data.items || []).map((item, i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-slate-700">{item}</p>
        </div>
      ))}
    </div>
  </div>
);

// ---- Slide : Comparison (Do vs Don't) -----------------------------------
const ComparisonSlide = ({ data, theme }) => (
  <div className="h-full w-full px-12 py-10 bg-white">
    <p className={`text-xs uppercase tracking-[0.3em] font-semibold mb-1 ${theme.accent}`}>Comparatif</p>
    <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
    <div className="grid grid-cols-2 gap-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          <h3 className="font-semibold text-emerald-700">À FAIRE</h3>
        </div>
        <div className="space-y-2">
          {(data.do || []).map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-slate-700">{item}</div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-3">
          <XCircle className="w-5 h-5 text-red-600" />
          <h3 className="font-semibold text-red-700">À ÉVITER</h3>
        </div>
        <div className="space-y-2">
          {(data.dont || []).map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-slate-700">{item}</div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ---- Slide : Quiz interactif --------------------------------------------
const QuizSlide = ({ data, theme }) => {
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="h-full w-full px-12 py-10 bg-white flex flex-col">
      <p className={`text-xs uppercase tracking-[0.3em] font-semibold mb-1 ${theme.accent}`}>Quiz</p>
      <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
      <p className="text-lg text-slate-700 mb-6 max-w-4xl">{data.question}</p>
      <div className="space-y-3 max-w-3xl">
        {(data.options || []).map((opt, i) => {
          const isCorrect = revealed && i === data.answerIndex;
          const isWrong = revealed && selected === i && i !== data.answerIndex;
          return (
            <button
              key={i}
              onClick={() => { setSelected(i); setRevealed(true); }}
              disabled={revealed}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                isCorrect ? 'border-emerald-500 bg-emerald-50' :
                isWrong ? 'border-red-500 bg-red-50' :
                selected === i ? 'border-slate-400 bg-slate-50' :
                'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`quiz-option-${i}`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${isCorrect ? 'bg-emerald-600 text-white' : isWrong ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="text-base text-slate-800">{opt}</span>
                {isCorrect && <CheckCircle className="w-5 h-5 ml-auto text-emerald-600" />}
                {isWrong && <XCircle className="w-5 h-5 ml-auto text-red-600" />}
              </div>
            </button>
          );
        })}
      </div>
      {revealed && data.explanation && (
        <div className={`mt-6 px-4 py-3 rounded-lg border-l-4 ${theme.noteBorder} bg-slate-50 text-sm text-slate-700 max-w-3xl`}>
          <strong>Explication :</strong> {data.explanation}
        </div>
      )}
    </div>
  );
};

// ---- Slide : Takeaways (à retenir) --------------------------------------
const TakeawaysSlide = ({ data, theme }) => (
  <div className={`h-full w-full px-12 py-10 bg-gradient-to-br ${theme.gradient}`}>
    <div className="flex items-center gap-3 mb-2">
      <Target className={`w-6 h-6 ${theme.iconColor}`} />
      <p className={`text-xs uppercase tracking-[0.3em] font-semibold ${theme.accent}`}>À retenir</p>
    </div>
    <h2 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>{data.title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {(data.items || []).map((item, i) => (
        <div key={i} className="bg-white/80 backdrop-blur p-5 rounded-2xl border border-white shadow-sm">
          <div className={`w-10 h-10 rounded-xl ${theme.iconBg} flex items-center justify-center font-bold text-lg ${theme.iconColor} mb-3`}>{i + 1}</div>
          <p className="text-base font-medium text-slate-800">{item}</p>
        </div>
      ))}
    </div>
  </div>
);

// ---- Slide : CTA final --------------------------------------------------
const CtaSlide = ({ data, theme }) => (
  <div className={`h-full w-full flex flex-col items-center justify-center text-center px-12 py-10 bg-gradient-to-br ${theme.gradient}`}>
    <Award className={`w-16 h-16 ${theme.iconColor} mb-4`} />
    <h2 className="text-5xl font-extrabold text-slate-900 leading-tight mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {data.title || 'Module terminé !'}
    </h2>
    <p className="text-xl text-slate-700 max-w-3xl mb-8">{data.subtitle}</p>
    <div className="flex flex-wrap items-center justify-center gap-3">
      {(data.actions || []).map((act, i) => (
        <a
          key={i}
          href={act.href}
          className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-white ${theme.ctaBg} hover:brightness-110 transition-all shadow-md`}
          data-testid={`cta-action-${i}`}
        >
          {act.label} <ArrowRight className="w-4 h-4" />
        </a>
      ))}
    </div>
  </div>
);

const SLIDE_TYPES = {
  cover: CoverSlide,
  intro: IntroSlide,
  concept: ConceptSlide,
  screenshot: ScreenshotSlide,
  steps: StepsSlide,
  checklist: ChecklistSlide,
  comparison: ComparisonSlide,
  quiz: QuizSlide,
  takeaways: TakeawaysSlide,
  cta: CtaSlide,
};

const SlideRenderer = ({ slide, theme }) => {
  const Component = SLIDE_TYPES[slide.type];
  if (!Component) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-white text-red-600 px-12">
        Type de slide inconnu : <code className="ml-2 font-mono">{slide.type}</code>
      </div>
    );
  }
  return <Component data={slide} theme={theme} />;
};

export default SlideRenderer;
export { SLIDE_TYPES };
